<template>
  <div class="organization__info">

    <tabs pills
          class="mt-2"
          type="secondary"
          tab-nav-classes="justify-content-start"
          tab-content-classes="tab-space">
      <tab-pane :title="$t('organization.infoTypes.image')" id="image">

        <data-table :entity="entity"
                    :data="getData('image')"
                    :columns="imageColumns"
                    operations="add,delete"
                    :disable-permission-checks="true"
                    :allowApiDelete="false"
                    :go-to-details-on-row-click="false"
                    :go-to-add-on-click="false"
                    :show-confirm-before-delete="false"
                    :clickable="false"
                    @on-add="addRow('image')"
                    @on-delete="deleteRow"
                    ref="table">
          <template v-slot:order="{row}">
            <fg-input placeholder=""
                      @blur="saveOrUpdateInfo(row)"
                      v-model="row.order"
                      type="number">
            </fg-input>
          </template>
          <template v-slot:image="{row, index}">
            <image-upload :src="row.value.value"
                          size-type="upload"
                          slot="editable-component"
                          @on-change="(val) => updateImage(val, 'image', row)">
            </image-upload>
          </template>
        </data-table>
      </tab-pane>
      <tab-pane :title="$t('organization.infoTypes.video')" id="video">

        <data-table :entity="entity"
                    :data="getData('video')"
                    :columns="videoColumns"
                    operations="add,delete"
                    :disable-permission-checks="true"
                    :allowApiDelete="false"
                    :go-to-details-on-row-click="false"
                    :go-to-add-on-click="false"
                    :show-confirm-before-delete="false"
                    :clickable="false"
                    @on-add="addRow('video')"
                    @on-delete="deleteRow"
                    ref="table">
          <template v-slot:order="{row}">
            <fg-input placeholder=""
                      @blur="saveOrUpdateInfo(row)"
                      v-model="row.order"
                      type="number">
            </fg-input>
          </template>
          <template v-slot:link="{row, index}">
            <fg-input :placeholder="$t('organizationNames.labels.link')"
                      v-model="row.value.value"
                      @blur="saveOrUpdateInfo(row)"
                      :error="(validVideoUrl(row))">
            </fg-input>
          </template>

          <template v-slot:preview="{row}">
            <div class="video-player">
              <youtube v-if="isYoutubeVideo(row)"
                       :video-id="getVideoId(row)"
                       player-width="300px"
                       player-height="250px">
              </youtube>
              <iframe v-if="isVimeoVideo(row)"
                      :src="`https://player.vimeo.com/video/${getVideoId(row)}?color=ff0179`"
                      width="300" height="250" frameborder="0" allow="autoplay; fullscreen" allowfullscreen></iframe>
            </div>
          </template>

        </data-table>
      </tab-pane>
      <tab-pane :title="$t('organization.infoTypes.social')" id="social">

        <data-table :entity="entity"
                    :data="getData('social')"
                    :columns="socialColumns"
                    operations="add,delete"
                    :disable-permission-checks="true"
                    :allowApiDelete="false"
                    :go-to-details-on-row-click="false"
                    :go-to-add-on-click="false"
                    :show-confirm-before-delete="false"
                    :clickable="false"
                    @on-add="addRow('social')"
                    @on-delete="deleteRow"
                    ref="table">
          <template v-slot:order="{row}">
            <fg-input placeholder=""
                      @blur="saveOrUpdateInfo(row)"
                      v-model="row.order"
                      type="number">
            </fg-input>
          </template>
          <template v-slot:name="{row, index}">
            <fg-input>
              <el-select v-model="row.value.name"
                         @change="saveOrUpdateInfo(row)"
                         allow-create
                         filterable
                         :placeholder="$t('organizationNames.labels.name')"
                         class="select-default">
                <el-option v-for="option in socialTypes"
                           :key="option.label"
                           :label="option.label"
                           :value="option.value"
                           class="select-default">
                </el-option>
              </el-select>
            </fg-input>
          </template>

          <template v-slot:link="{row, index}">
            <fg-input :placeholder="$t('organizationNames.labels.link')"
                      @blur="saveOrUpdateInfo(row)"
                      v-model="row.value.value"
                      :error="(validUrl(row))">
            </fg-input>
          </template>
        </data-table>
      </tab-pane>
      <tab-pane :title="$t('organization.infoTypes.location')" id="location">
        <data-table :entity="entity"
                    :data="getData('location')"
                    :columns="locationColumns"
                    operations="add,delete"
                    :disable-permission-checks="true"
                    :allowApiDelete="false"
                    :go-to-details-on-row-click="false"
                    :go-to-add-on-click="false"
                    :show-confirm-before-delete="false"
                    :clickable="false"
                    @on-add="addRow('location')"
                    @on-delete="deleteRow"
                    ref="table">
          <template v-slot:order="{row}">
            <fg-input placeholder=""
                      @blur="saveOrUpdateInfo(row)"
                      v-model="row.order"
                      type="number">
            </fg-input>
          </template>
          <template v-slot:location="{row, index}">
            <fg-input :placeholder="$t('organizationNames.labels.location')"
                      :id="row.id ? row.id.toString() : index.toString()"
                      component="vue-google-autocomplete"
                      autocomplete="new-password"
                      @blur="saveOrUpdateInfo(row)"
                      @change="val => onLocationChange(val, row)"
                      @placechanged="val => onPlaceChanged(val, row)"
                      v-model="row.value.value">
            </fg-input>
          </template>
          <template v-slot:locationName="{row, index}">
            <fg-input :placeholder="$t('organizationNames.labels.locationName')"
                      @blur="saveOrUpdateInfo(row)"
                      v-model="row.value.name">
            </fg-input>
          </template>
        </data-table>
      </tab-pane>

      <tab-pane :title="$t('organization.infoTypes.benefit')" id="benefit">

        <data-table :entity="entity"
                    :data="getData('benefit')"
                    :columns="benefitsColumns"
                    operations="add,delete"
                    :disable-permission-checks="true"
                    :allowApiDelete="false"
                    :go-to-details-on-row-click="false"
                    :go-to-add-on-click="false"
                    :show-confirm-before-delete="false"
                    :clickable="false"
                    @on-add="addRow('benefit')"
                    @on-delete="deleteRow"
                    ref="table">
          <template v-slot:order="{row}">
            <fg-input placeholder=""
                      @blur="saveOrUpdateInfo(row)"
                      v-model="row.order"
                      type="number">
            </fg-input>
          </template>
          <template v-slot:benefit="{row, index}">
            <fg-input :placeholder="$t('organizationNames.labels.benefit')"
                      @blur="saveOrUpdateInfo(row)"
                      v-model="row.value.value">
            </fg-input>
          </template>
          <template v-slot:icon="{row, index}">
            <icon-autocomplete v-model="row.value.icon">

            </icon-autocomplete>
          </template>
        </data-table>
      </tab-pane>
      <tab-pane :title="$t('organization.infoTypes.text')" id="text">

        <data-table :entity="entity"
                    :data="getData('text')"
                    :columns="textColumns"
                    operations="add,delete"
                    :disable-permission-checks="true"
                    :allowApiDelete="false"
                    :go-to-details-on-row-click="false"
                    :go-to-add-on-click="false"
                    :show-confirm-before-delete="false"
                    :clickable="false"
                    @on-add="addRow('text')"
                    @on-delete="deleteRow"
                    ref="table">
          <template v-slot:order="{row}">
            <fg-input placeholder=""
                      @blur="saveOrUpdateInfo(row)"
                      v-model="row.order"
                      type="number">
            </fg-input>
          </template>
          <template v-slot:text="{row, index}">
            <fg-input :placeholder="$t('organizationNames.labels.text')">
              <wysiwyg :placeholder="$t('organizationNames.labels.text')"
                       class="form-control"
                       :key="row.key"
                       @blur="saveOrUpdateInfo(row)"
                       v-model="row.value.value">
              </wysiwyg>
            </fg-input>
          </template>
        </data-table>
      </tab-pane>
    </tabs>
  </div>
</template>
<script>
  import Vue from 'vue'
  import VueYouTubeEmbed from 'vue-youtube-embed'
  import VueGoogleAutocomplete from 'src/components/Autocomplete/VueGoogleAutocomplete'
  import IconAutocomplete from 'src/components/Autocomplete/IconAutocomplete'
  import { Tabs, TabPane } from 'src/components'
  import { Select, Option } from 'element-ui'
  import EditableGridCell from 'src/components/EditableGridCell'
  import Wysiwyg from 'src/components/Wysiwyg/RedactorWysiwyg'
  import ImageUpload from 'src/components/ImageUpload'
  import entities from 'src/config/entities'
  import { url } from 'vee-validate/dist/rules.esm'
  Vue.use(VueYouTubeEmbed)
  Vue.component(VueGoogleAutocomplete.name, VueGoogleAutocomplete)
  import { getIdFromURL } from 'vue-youtube-embed'
  import OrganizationService from 'src/services/OrganizationService'
  export default {
    name: 'organization-infos',
    components: {
      Tabs,
      TabPane,
      Wysiwyg,
      EditableGridCell,
      ImageUpload,
      IconAutocomplete,
      [Select.name]: Select,
      [Option.name]: Option,
    },
    props: {
      info: {
        type: Array,
        default: () => []
      }
    },
    model: {
      prop: 'info',
      event: 'cahnge'
    },
    data () {
      return {
        entity: entities.ORGANIZATION_INFO,
        selectedInfoType: 'image',
        socialTypes: [
          {
            label: this.$t('organization.socialTypes.facebook'),
            value: this.$t('organization.socialTypes.facebook')
          },
          {
            label: this.$t('organization.socialTypes.linkedin'),
            value: this.$t('organization.socialTypes.linkedin')
          },
          {
            label: this.$t('organization.socialTypes.twitter'),
            value: this.$t('organization.socialTypes.twitter')
          },
          {
            label: this.$t('organization.socialTypes.instagram'),
            value: this.$t('organization.socialTypes.instagram')
          },
          {
            label: this.$t('organization.socialTypes.medium'),
            value: this.$t('organization.socialTypes.medium')
          },
          {
            label: this.$t('organization.socialTypes.website'),
            value: this.$t('organization.socialTypes.website')
          },
        ],
        companyInfoTypes: [
          {
            label: this.$t('organization.infoTypes.image'),
            value: 'image'
          },
          {
            label: this.$t('organization.infoTypes.video'),
            value: 'video'
          },
          {
            label: this.$t('organization.infoTypes.social'),
            value: 'social'
          },
          {
            label: this.$t('organization.infoTypes.location'),
            value: 'location'
          },
          {
            label: this.$t('organization.infoTypes.benefit'),
            value: 'benefit'
          },
          {
            label: this.$t('organization.infoTypes.text'),
            value: 'text'
          }
        ],
        infoData: []
      }
    },
    computed: {
      organizationId() {
        return this.$route.params.id
      },
      imageColumns () {
        return [
          {
            prop: 'order',
            label: this.$t('organizationNames.labels.order'),
            width: '90px',
            sortable: true
          },
          {
            prop: 'image',
            label: this.$t('organizationNames.labels.image'),
            minWidth: '200px',
            sortable: true
          },
        ]
      },
      socialColumns () {
        return [
          {
            prop: 'order',
            label: this.$t('organizationNames.labels.order'),
            width: '90px',
            sortable: true
          },
          {
            prop: 'name',
            label: this.$t('organizationNames.labels.name'),
            minWidth: '170px',
            sortable: true
          },
          {
            prop: 'link',
            label: this.$t('organizationNames.labels.link'),
            minWidth: '250px',
            sortable: true
          },
        ]
      },
      videoColumns () {
        return [
          {
            prop: 'order',
            label: this.$t('organizationNames.labels.order'),
            width: '100px',
            sortable: true
          },
          {
            prop: 'link',
            label: this.$t('organizationNames.labels.link'),
            minWidth: '300px',
            sortable: true
          },
          {
            prop: 'preview',
            label: this.$t('organizationNames.labels.preview'),
            minWidth: '250px',
            sortable: true
          },
        ]
      },
      locationColumns () {
        return [
          {
            prop: 'order',
            label: this.$t('organizationNames.labels.order'),
            width: '90px',
            sortable: true
          },
          {
            prop: 'location',
            label: this.$t('organizationNames.labels.location'),
            minWidth: '260px',
            sortable: true
          },
          {
            prop: 'locationName',
            label: this.$t('organizationNames.labels.locationName'),
            minWidth: '180px',
            sortable: true
          },
        ]
      },
      benefitsColumns () {
        return [
          {
            prop: 'order',
            label: this.$t('organizationNames.labels.order'),
            width: '90px',
            sortable: true
          },
          {
            prop: 'benefit',
            label: this.$t('organizationNames.labels.benefit'),
            minWidth: '200px',
            sortable: true
          },
          {
            prop: 'icon',
            label: this.$t('organizationNames.labels.icon'),
            minWidth: '120px',
            sortable: true
          },
        ]
      },
      textColumns () {
        return [
          {
            prop: 'order',
            label: this.$t('organizationNames.labels.order'),
            width: '90px',
            sortable: true
          },
          {
            prop: 'text',
            label: this.$t('organizationNames.labels.text'),
            minWidth: '200px',
            sortable: true
          },
        ]
      },
      getData () {
        return type => this.infoData.filter(i => i.type === type)
      }
    },
    methods: {
      validUrl (row) {
        let valid = url.validate(row.value.value)
        row.value.valid = valid
        return valid ? '' : this.$t('organization.socialTypes.invalidUrl')
      },
      validVideoUrl(row) {
        let valid = url.validate(row.value.value)
        if (valid) {
          valid = row.value.value.includes('youtube') || row.value.value.includes('vimeo')
        }
        row.value.valid = valid
        return valid ? '' : this.$t('organization.socialTypes.invalidUrl')
      },
      isYoutubeVideo(row) {
        let link = row.value.value || '';
        return link.includes('youtube')
      },
      isVimeoVideo(row) {
        let link = row.value.value || '';
        return link.includes('vimeo')
      },
      getVideoId(row) {
        let link = row.value.value || '';
        if (this.isYoutubeVideo(row)) {
          return getIdFromURL(link)
        }
        if (this.isVimeoVideo(row)) {
          const parts =  link.split('/')
          return parts[parts.length - 1]
        }
        return ''
      },
      async updateImage (value, type, row) {
        row.value.value = value
        await this.saveOrUpdateInfo(row)
      },
      async addRow (type) {
        let row = {
          type,
          key: this.infoData.length,
          order: null,
          value: {
            value: ''
          }
        }
        if (type === 'social') {
          row.value.name = ''
        }
        if (type === 'location') {
          row.value.lat = null
          row.value.lng = null
        }

        if (type === 'benefit') {
          row.value.icon = 'PackageIcon'
        }
        this.infoData.push(row)
      },
      async deleteRow (row) {
        let index = this.infoData.findIndex(d => {
          if (d.id) {
            return d.id === row.id
          }
          return d.key === row.key
        })
        if (index !== -1) {
          this.infoData.splice(index, 1)
          if (!row.id) {
            return
          }
          await OrganizationService.deleteInfo(this.organizationId, row)
        }
      },
      async saveOrUpdateInfo(row) {
        if (row.id) {
          await OrganizationService.updateInfo(this.organizationId, row)
        } else if(row.creating){
          let index = this.infoData.findIndex(i => i.key === row.key)
          if (index !== -1) {
            setTimeout(() => {
              this.saveOrUpdateInfo(this.infoData[index])
            }, 500)
          }
        } else {
          row.creating = true
          let {data} = await OrganizationService.createInfo(this.organizationId, row)
          row.creating = false
          let index = this.infoData.findIndex(i => i.key === row.key)
          if (index !== -1) {
            this.infoData.splice(index, 1, data)
          }
        }
      },
      async onPlaceChanged(place, row) {
        let {longitude, latitude} = place
        if (latitude || longitude) {
          row.value.lat = latitude
          row.value.lng = longitude
        }
        await this.saveOrUpdateInfo(row)
      },
      async onLocationChange(location, row) {
        if (!location) {
          return
        }
        row.value.value = location
      }
    },
    watch: {
      info: {
        immediate: true,
        handler: function(newVal) {
          if (newVal) {
            this.infoData = newVal
          }
        }
      },
      infoData(newVal) {
        this.$emit('change', newVal)
      }
    }
  }
</script>
<style lang="scss">
  .organization__info {
    .el-table td > div.cell div {
      width: 100%;
    }

    .el-table td > div.cell {
      min-height: 80px;
      display: flex;
      align-items: flex-start;
    }
    .el-table .table-buttons {
      margin-top: 7px;
    }

  }
</style>
